import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/formatter";
import Style from "./addGasStationModal.module.css";

const PricingStructureTable = ({ 
  pricingStructure, 
  currencyDisplay, 
  onPricingValueUpdate,
  disabled = false,
  showWrapper = true,
  countryCode = "AT"
}) => {
  const { t } = useTranslation();
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  if (!pricingStructure || !pricingStructure.fuelTypes) return null;

  const rows = [
    {
      label: `${t("GasStations.generalProcessingAbsolute")} ${currencyDisplay}`,
      field: 'processingFeeFlat'
    },
    {
      label: `${t("GasStations.generalProcessingPercentage")}`,
      field: 'processingFeePercentage'
    },
    {
      label: `${t("GasStations.warning")} ${currencyDisplay}`,
      field: 'reminderFee'
    },
    {
      label: `${t("GasStations.lastWarning")} ${currencyDisplay}`,
      field: 'lastReminderFee'
    },
    {
      label: `${t("GasStations.withinMunicipality")} ${currencyDisplay}`,
      field: 'addressSurchargeWithinMunicipality'
    },
    {
      label: `${t("GasStations.outsideMunicipality")} ${currencyDisplay}`,
      field: 'addressSurchargeOutsideMunicipality'
    },
    {
      label: `${t("GasStations.abroad")} ${currencyDisplay}`,
      field: 'addressSurchargeAbroad'
    }
  ];

  const handleBlur = (colIndex, field, rawValue) => {
    setEditingCell(null);
    setEditingValue("");
    
    // Convert any comma to dot for parsing
    let cleanValue = String(rawValue).replace(',', '.');
    
    // Remove any characters that aren't digits, dots, or minus signs
    cleanValue = cleanValue.replace(/[^\d.-]/g, '');
    
    // Handle multiple dots - keep only the first one
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Parse to number
    const numericValue = parseFloat(cleanValue);
    
    // If invalid, default to 0, otherwise round to 2 decimals
    const finalValue = isNaN(numericValue) ? 0 : parseFloat(numericValue.toFixed(2));
    
    onPricingValueUpdate(colIndex, field, finalValue);
  };

  const tableContent = (
    <>
      {showWrapper && (
        <div className={Style.section__title}>
          {t("GasStations.pricingStructure")}
        </div>
      )}
      
      <div className={Style.tableContainer}>
        <table className={Style.pricingTable}>
          <thead>
            <tr>
              <th>{t("GasStations.maxFuelValue")} {currencyDisplay}</th>
              {pricingStructure.fuelTypes.map((fuelType, index) => (
                <th key={index}>{fuelType.maxFuelValue}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className={Style.rowLabel}>{row.label}</td>
                {pricingStructure.fuelTypes.map((fuelType, colIndex) => {
                  const cellKey = `${rowIndex}-${colIndex}`;
                  const isEditing = editingCell === cellKey;
                  const rawValue = fuelType[row.field];
                  
                  // Show editing value when editing, formatted value otherwise
                  const displayValue = isEditing 
                    ? editingValue
                    : formatCurrency(parseFloat(rawValue) || 0, countryCode);
                  
                  return (
                    <td key={colIndex}>
                      <input
                        type="text"
                        value={displayValue}
                        onChange={(e) => {
                          // Allow typing anything - store in local state
                          setEditingValue(e.target.value);
                        }}
                        onFocus={() => {
                          setEditingCell(cellKey);
                          // Initialize editing value with current raw value
                          setEditingValue(String(rawValue));
                        }}
                        onBlur={() => handleBlur(colIndex, row.field, editingValue)}
                        disabled={disabled}
                        className={Style.tableInput}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  return showWrapper ? (
    <div className={Style.section}>
      {tableContent}
    </div>
  ) : (
    tableContent
  );
};

export default PricingStructureTable;